/* =============================================================================
   DiSEGNO Builder — Geometry engine
   -----------------------------------------------------------------------------
   Pure functions in a virtual 1000 x 900 space. NO DOM, NO analytics, NO
   Shopify. Given an ordered element list + jewelry type + starter, returns
   positioned pieces, insertion slots, chain/clasp geometry and length
   milestones. The renderer (disegno.builder.js) maps this to SVG/HTML.

   Carried over verbatim (values + maths) from the approved bracelet/necklace
   prototype so the desktop/mobile UI is unchanged. Adds `earrings` mode.

   FIXED PHYSICAL SCALE: element size = effectivePitch * (span / maxLength) *
   PITCH_IMG_RATIO — constant, never varies with element count.

   CONNECTION ORIENTATION (unchanged from the approved model):
     bracelet / necklace : run reads LEFT -> RIGHT along the loop; each element's
       jump ring (baked into the right ~20% of its art) is engaged by the
       recessed hook UNDER the element to its right; the fixed starter diamond is
       the RIGHT (last) element, where the run meets the 1.5mm flat snake chain.
     earrings : elements hang VERTICALLY, top -> bottom. The starter round-diamond
       stud is the TOP element with a hidden hook beneath it; each lower element
       has its receiving jump ring at its TOP, connecting upward to the hook
       above. No visible connector lines are drawn — overlap + z-order imply the
       join.
   ========================================================================== */
(function (root) {
  'use strict';

  var Disegno = (root.Disegno = root.Disegno || {});
  if (Disegno.Geometry) return;

  var VW = 1000, VH = 900;
  var PITCH_IMG_RATIO = 1.24;
  var DEFAULT_PITCH_MM = 7.39;

  /* Per-jewelry-type geometry descriptors. milestonesMm / claspAtMm / maxLengthMm
     match the approved prototype for bracelet & necklace. Earrings use a drop
     length in mm (typical DiSEGNO drop 12–55 mm). All merchant-overridable via
     window.DisegnoConfig.geometry. */
  var TYPES = {
    bracelet: {
      key: 'bracelet',
      loop: { cx: 500, cy: 430, rx: 300, ry: 300, beadDrop: 32, tail: 128 },
      milestonesMm: [160, 170, 180, 190],
      milestoneLabels: ['16 CM', '17 CM', '18 CM', '19 CM'],
      claspAtMm: 160, maxLengthMm: 190, defaultTargetMm: 160,
      orientation: 'loop'
    },
    necklace: {
      key: 'necklace',
      loop: { cx: 500, cy: 408, rx: 445, ry: 300, beadDrop: 28, tail: 116 },
      milestonesMm: [406.4, 431.8, 457.2, 482.6, 508],
      milestoneLabels: ['16"', '17"', '18"', '19"', '20"'],
      claspAtMm: 406.4, maxLengthMm: 508, defaultTargetMm: 406.4,
      orientation: 'loop'
    },
    earrings: {
      key: 'earrings',
      // vertical drop column, per ear. x is offset for the left/right ear pair.
      column: { topY: 150, xLeft: 360, xRight: 640, xSingle: 500, gapMm: 0.6 },
      milestonesMm: [18, 30, 42, 55],
      milestoneLabels: ['18 MM', '30 MM', '42 MM', '55 MM'],
      claspAtMm: 999, maxLengthMm: 60, defaultTargetMm: 30,
      orientation: 'vertical',
      studAtTop: true
    }
  };

  function types() {
    var over = (root.DisegnoConfig && root.DisegnoConfig.geometry) || {};
    var out = {};
    for (var k in TYPES) out[k] = merge(clone(TYPES[k]), over[k] || {});
    return out;
  }
  function typeOf(jt) { return types()[jt] || types().bracelet; }

  var clamp = function (n, a, b) { return Math.max(a, Math.min(b, n)); };
  function pitchOf(el) { return (el && (el.effective_pitch_mm || el.effectivePitchMm || el.visual_footprint)) || DEFAULT_PITCH_MM; }
  function ratioOf(el) { return (el && (el.ratio || el.art_ratio)) || 1; }
  function scaleOf(el) { return (el && (el.display_scale != null ? el.display_scale : 1)) || 1; }

  function perimeter(g) {
    if (g.rx === g.ry) return 2 * Math.PI * g.rx;
    var a = g.rx, b = g.ry, h = Math.pow((a - b) / (a + b), 2);
    return Math.PI * (a + b) * (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h)));
  }
  function ptAt(g, t, k) {
    if (k == null) k = 1;
    var phi = t * 2 * Math.PI;
    return { x: g.cx + g.rx * k * Math.sin(phi), y: g.cy - g.ry * k * Math.cos(phi) };
  }
  function tOf(g, x, y) {
    var a = Math.atan2(x - g.cx, -(y - g.cy));
    return ((a / (2 * Math.PI)) % 1 + 1) % 1;
  }

  /* =========================================================================
     layout(input) -> geometry
     input = {
       jewelryType: 'bracelet'|'necklace'|'earrings',
       seeded: bool,                      // starter selected
       // bracelet/necklace: ordered customer elements (NOT incl. seed)
       elements: [Element],
       // earrings: ordered per ear (NOT incl. the shared stud seed)
       left_earring: [Element], right_earring: [Element],
       earMode: 'single'|'pair'|'asymmetrical',
       seedEl: Element                    // the starter/stud element
     }
     ========================================================================= */
  function layout(input) {
    var jt = input.jewelryType || 'bracelet';
    if (jt === 'earrings') return layoutEarrings(input);
    return layoutLoop(input, jt);
  }

  /* ---- bracelet / necklace (loop) — unchanged approved model ------------- */
  function layoutLoop(input, jt) {
    var T = typeOf(jt);
    var g = T.loop;
    var seeded = !!input.seeded;
    var user = (input.elements || []).slice();
    // fixed starter diamond APPENDED at the RIGHT (last) end
    var list = seeded && input.seedEl ? user.concat([wrapSeed(input.seedEl)]) : user;

    var P = perimeter(g);
    var maxLen = T.maxLengthMm;
    var mmToPx = P / maxLen;
    var occ = list.reduce(function (s, x) { return s + pitchOf(x.el || x); }, 0);
    var full = seeded && occ >= T.claspAtMm - 0.01;
    var runSpan = clamp(occ / maxLen, 0, 1);
    var startT = -runSpan / 2;

    var cum = 0;
    var els = list.map(function (x, i) {
      var el = x.el || x;
      var wmm = pitchOf(el);
      var tMid = startT + (cum + wmm / 2) / maxLen;
      cum += wmm;
      var wpx = wmm * mmToPx * PITCH_IMG_RATIO * scaleOf(el);
      var hpx = wpx / ratioOf(el);
      var p = ptAt(g, tMid);
      return {
        uid: x.uid || el.uid || ('el' + i),
        el: el, i: i,
        x: p.x, y: p.y, rot: tMid * 360, w: wpx, h: hpx, tMid: tMid,
        seed: seeded && i === list.length - 1,
        z: i
      };
    });

    var n = els.length;
    var maxSlot = seeded ? n - 1 : n;      // no slot to the right of the fixed seed
    var slots = [];
    for (var k = 0; k <= maxSlot; k++) {
      var tk;
      if (n === 0) tk = 0;
      else if (k === 0) tk = els[0].tMid - (els[0].w / P) * 0.6;
      else if (k === n) tk = els[n - 1].tMid + (els[n - 1].w / P) * 0.6;
      else tk = (els[k - 1].tMid + els[k].tMid) / 2;
      var sp = ptAt(g, tk);
      slots.push({ index: k, t: tk, x: sp.x, y: sp.y, rot: tk * 360 });
    }

    var milestones = T.milestonesMm.map(function (mm, idx) {
      return { mm: mm, label: T.milestoneLabels[idx], t: mm / (2 * maxLen), reached: occ >= mm - 0.01 };
    });

    return {
      jewelryType: jt, orientation: 'loop',
      g: g, perimeter: P, mmToPx: mmToPx, maxLengthMm: maxLen,
      totalLenMm: occ, runSpan: runSpan, full: full,
      els: els, slots: slots, milestones: milestones,
      chainLenMm: Math.max(0, T.claspAtMm - occ),
      chainStartT: runSpan / 2, chainEndT: 1 - runSpan / 2,
      bead: { x: g.cx, y: g.cy + g.ry + g.beadDrop, tail: g.tail },
      clasp: { x: g.cx, y: g.cy + g.ry }
    };
  }

  /* ---- earrings (vertical) --------------------------------------------------
     CORRECTED mechanical model:
       - The STARTER STUD is a special component: clean round diamond, NO
         receiving ring, concealed rear hook, earring post, FIXED top anchor
         (never draggable / reorderable / mid-stack). It is NOT rotated.
       - Every MODULE is rotated -90deg so its receiving jump ring (which sits on
         the RIGHT edge in bracelet/necklace art) now points UP.
       - Each module is raised so its top (ring zone) tucks CONCEAL behind the
         piece directly above it; z-order (upper piece in front) hides the ring.
       - No connectors are drawn, no chain, no floating gaps.
     Each column: { side, x, stud:{...}, els:[positioned modules], slots, dropMm }.
     ------------------------------------------------------------------------ */
  // Fraction of a module's vertical extent hidden behind the piece above: hides
  // ~75% of the top-facing receiving ring, leaves ~25% visible, no stone overlap
  // (matches bracelet/necklace spacing feel).
  var EAR_CONCEAL = 0.16;

  function layoutEarrings(input) {
    var T = typeOf('earrings');
    var c = T.column;
    var mode = input.earMode || (input.earringQuantity === 1 ? 'single' : (input.matchEarrings === false ? 'asymmetrical' : 'pair'));
    var studEl = input.seedEl || null;

    function column(side, arr, x) {
      var user = (arr || []).slice();
      var colH = VH - c.topY - 90;
      var mmToPx = colH / T.maxLengthMm;

      // --- stud: fixed top anchor, no ring, not rotated ---
      var studVext = studEl ? pitchOf(studEl) * mmToPx * PITCH_IMG_RATIO * scaleOf(studEl) : 0;
      var studCy = c.topY + studVext / 2;
      var stud = studEl ? {
        uid: '__stud__' + side, el: studEl, isStud: true, seed: true, side: side,
        x: x, cx: x, cy: studCy, w: studVext, h: studVext, vext: studVext,
        rot: 0, ring: 'none', z: 999
      } : null;

      // --- modules: rotated -90 (ring faces UP), stacked with concealment overlap ---
      var prevBottom = studCy + studVext / 2;
      var dropMm = 0;
      var els = user.map(function (m, i) {
        var el = m.el || m;
        var imgW = pitchOf(el) * mmToPx * PITCH_IMG_RATIO * scaleOf(el);   // -> vertical extent after -90deg
        var imgH = imgW / ratioOf(el);                                     // -> horizontal extent
        var vext = imgW;
        var cy = prevBottom + vext / 2 - vext * EAR_CONCEAL;
        prevBottom = cy + vext / 2;
        dropMm += pitchOf(el);
        return {
          uid: m.uid || el.uid || (side + i),
          el: el, i: i, side: side,
          x: x, cx: x, cy: cy,
          w: imgW, h: imgH, vext: vext,
          rot: -90, ring: 'top',        // receiving ring at the TOP, concealed by the piece above
          z: 800 - i,                    // each module in front of the one below it
          seed: false
        };
      });
      // insertion slots — index into the MODULE list; NO slot above the fixed stud
      var slots = [];
      for (var k = 0; k <= els.length; k++) {
        var sy;
        if (els.length === 0) sy = studCy + studVext * 0.60;
        else if (k === 0) sy = els[0].cy - els[0].vext * 0.15;
        else if (k === els.length) sy = els[els.length - 1].cy + els[els.length - 1].vext / 2 + 14;
        else sy = (els[k - 1].cy + els[k].cy) / 2;
        slots.push({ index: k, x: x, y: sy });
      }
      return { side: side, x: x, stud: stud, els: els, slots: slots, dropMm: dropMm, mmToPx: mmToPx };
    }

    var ears;
    if (mode === 'single') {
      ears = [column('single', input.left_earring || input.right_earring || [], c.xSingle)];
    } else {
      ears = [
        column('left', input.left_earring || [], c.xLeft),
        column('right', input.right_earring || [], c.xRight)
      ];
    }

    var maxDrop = Math.max.apply(null, ears.map(function (e) { return e.dropMm; }).concat([0]));
    var milestones = T.milestonesMm.map(function (mm, i) {
      return { mm: mm, label: T.milestoneLabels[i], reached: maxDrop >= mm - 0.01 };
    });

    return {
      jewelryType: 'earrings', orientation: 'vertical',
      earMode: mode,
      earringQuantity: (mode === 'single') ? 1 : 2,
      matchEarrings: (mode === 'pair'),
      ears: ears,
      studs: ears.map(function (e) { return e.stud; }).filter(Boolean),
      totalLenMm: maxDrop, maxLengthMm: T.maxLengthMm,
      milestones: milestones,
      // convenience mirrors so shared renderer code can read a flat list
      // (studs FIRST per column, then that column's modules)
      els: ears.reduce(function (a, e) { return a.concat(e.stud ? [e.stud] : [], e.els); }, []),
      slots: ears.reduce(function (a, e) { return a.concat(e.slots.map(function (s) { return merge(clone(s), { side: e.side }); })); }, [])
    };
  }

  function wrapSeed(seedEl) {
    return { uid: '__seed__', el: seedEl, seed: true };
  }

  /* ---- helpers ---------------------------------------------------------- */
  function clone(o) { try { return JSON.parse(JSON.stringify(o)); } catch (e) { return o; } }
  function merge(a, b) {
    a = a || {};
    for (var k in (b || {})) {
      if (b[k] && typeof b[k] === 'object' && !Array.isArray(b[k]) && a[k] && typeof a[k] === 'object') a[k] = merge(a[k], b[k]);
      else a[k] = b[k];
    }
    return a;
  }

  Disegno.Geometry = {
    VW: VW, VH: VH, PITCH_IMG_RATIO: PITCH_IMG_RATIO,
    types: types, typeOf: typeOf,
    perimeter: perimeter, ptAt: ptAt, tOf: tOf,
    pitchOf: pitchOf, ratioOf: ratioOf,
    layout: layout
  };
})(typeof window !== 'undefined' ? window : this);
